import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import moment from 'moment';
import { IWorkshopAppointmentListItem } from 'interfaces/workshop-appointment-interface';
import { IOrganizationAddress } from './organizationSlice';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface ISlot {
  _id: string;
  organization_id: string;
  parking_id: string;
  date: string;
  start_time: string;
  end_time: string;
  workshop_capacity: number;
  booked_count?: number;
  repeat?: 'none' | 'weekly';
}

export interface IParkingSlotGroup {
  _id: string;
  parking_id: string;
  parking_details?: IOrganizationAddress & { address_title?: string };
  slots: Array<
    ISlot & {
      workshop_details?: IWorkshopAppointmentListItem[];
    }
  >;
}

export interface ICreateSlotItem {
  organization_id: string;
  parking_id: string;
  // ISO datetimes — the calendar day + local time-of-day already combined
  // (see combineLocalDateAndTime in helpers/utils).
  start_time: string;
  end_time: string;
  workshop_capacity: number;
  repeat: 'none' | 'weekly';
}

export interface ICreateSlotsRequest {
  slots: ICreateSlotItem[];
}

export interface ICreateSlotsResponse {
  success: boolean;
  success_code: 'SLOTS_CREATED_SUCCESSFULLY';
  message: string;
}

export interface IUpdateSlotRequest {
  workshop_capacity: number;
  start_time: string;
  end_time: string;
}

export interface IUpdateSlotResponse {
  success: boolean;
  success_code: 'SLOT_UPDATED_SUCCESSFULLY';
}

export interface IDeleteSlotRequest {
  delete_reason: string;
}

export interface IDeleteSlotResponse {
  success: boolean;
  success_code: 'SLOT_DELETED_SUCCESSFULLY';
}

export interface ISlotWorkshopsResponse {
  workshops: IWorkshopAppointmentListItem[];
  total_count: number;
}

export interface IParkingOption {
  _id: string;
  address_title: string;
  city?: string;
  state?: string;
  country?: string;
  address1?: string;
  address2?: string;
  zipcode?: string;
}

// ── State ──────────────────────────────────────────────────────────────────────

interface ISlotsState {
  calendarSlots: IParkingSlotGroup[];
  selectedSlot: ISlot | null;
  slotWorkshops: IWorkshopAppointmentListItem[];
  slotWorkshopsCount: number;
  orgParkings: IParkingOption[];
  availableBookingSlots: ISlot[];
}

const initialState: ISlotsState = {
  calendarSlots: [],
  selectedSlot: null,
  slotWorkshops: [],
  slotWorkshopsCount: 0,
  orgParkings: [],
  availableBookingSlots: [],
};

// ── Slice ──────────────────────────────────────────────────────────────────────

const slotsSlice = createSlice({
  name: 'Slots',
  initialState,
  reducers: {
    setCalendarSlots: (state, { payload }: { payload: IParkingSlotGroup[] }) => {
      state.calendarSlots = payload;
    },
    setSelectedSlot: (state, { payload }: { payload: ISlot | null }) => {
      state.selectedSlot = payload;
    },
    setSlotWorkshops: (
      state,
      { payload }: { payload: { workshops: IWorkshopAppointmentListItem[]; total_count: number } },
    ) => {
      state.slotWorkshops = payload.workshops;
      state.slotWorkshopsCount = payload.total_count;
    },
    setOrgParkings: (state, { payload }: { payload: IParkingOption[] }) => {
      state.orgParkings = payload;
    },
    clearSlotWorkshops: state => {
      state.slotWorkshops = [];
      state.slotWorkshopsCount = 0;
    },
    setAvailableBookingSlots: (state, { payload }: { payload: ISlot[] }) => {
      state.availableBookingSlots = payload;
    },
    clearAvailableBookingSlots: state => {
      state.availableBookingSlots = [];
    },
  },
});

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Backend now returns start_time/end_time as full ISO datetimes (no more `date`
 * field). Convert a raw API slot back into the `date` + "HH:mm" shape the rest
 * of the app expects.
 */
const normalizeSlot = <T extends { start_time: string; end_time: string }>(raw: T): T & { date: string } => ({
  ...raw,
  date: moment(raw.start_time).format('DD/MM/YYYY'),
  start_time: moment(raw.start_time).format('HH:mm'),
  end_time: moment(raw.end_time).format('HH:mm'),
});

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const createSlotAction =
  (payload: { request: ICreateSlotsRequest }) => async (_dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData }: { data: ICreateSlotsResponse } = await api.post('/admin/slots', request);
      return Promise.resolve(responseData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const editSlotAction =
  (payload: { slot_id: string; request: IUpdateSlotRequest }) => async (_dispatch: Dispatch) => {
    try {
      const { slot_id, request } = payload;
      const { data: responseData }: { data: IUpdateSlotResponse } = await api.put(`/admin/slot/${slot_id}`, request);
      return Promise.resolve(responseData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const deleteSlotAction =
  (payload: { slot_id: string; request: IDeleteSlotRequest }) => async (_dispatch: Dispatch) => {
    try {
      const { slot_id, request } = payload;
      const { data: responseData }: { data: IDeleteSlotResponse } = await api.patch(`/admin/slot/${slot_id}`, request);
      return Promise.resolve(responseData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getSlotWorkshops =
  (payload: { slot_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { slot_id } = payload;
      const { data: responseData } = await api.get(`/admin/workshops/${slot_id}`);
      dispatch(
        setSlotWorkshops({
          workshops: responseData?.data?.workshops || responseData?.data || [],
          total_count:
            responseData?.data?.total_count ??
            responseData?.data?.workshops?.length ??
            (Array.isArray(responseData?.data) ? responseData.data.length : 0),
        }),
      );
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

/**
 * Fetch slots for the calendar view using GET /admin/slots.
 * Passes view_type, day and organization_id (mandatory).
 * Accepts optional parking_id to filter by parking area.
 * With list_workshops=true the response includes nested workshop_details per slot.
 * The backend returns a flat array of slots (each with parking_details embedded).
 * This thunk groups them by parking_id into IParkingSlotGroup[] before storing.
 */
export const getCalendarSlotsAction =
  (
    payload: {
      view_type: 'daily' | 'weekly' | 'monthly';
      day: string;
      organization_id: string;
      parking_id?: string;
      list_workshops?: boolean;
    },
    signal?: AbortSignal,
  ) =>
    async (dispatch: Dispatch) => {
      try {
        const { view_type, day, organization_id, parking_id, list_workshops = true } = payload;
        let queryString = `?view_type=${view_type}&day=${encodeURIComponent(day)}&organization_id=${encodeURIComponent(organization_id)}`;
        if (list_workshops) {
          queryString += '&list_workshops=true';
        }
        if (parking_id) {
          queryString += `&parking_id=${encodeURIComponent(parking_id)}`;
        }
        const { data: responseData } = await api.get(`/admin/slots${queryString}`, { signal });

        // Backend returns a flat array; each slot has parking_details embedded.
        // Group into IParkingSlotGroup[] keyed by parking_id.
        const rawSlots: any[] = (responseData?.data || []).map(normalizeSlot);
        const groupMap: Record<string, IParkingSlotGroup> = {};
        rawSlots.forEach(slot => {
          const parkingId = slot.parking_id?._id || slot.parking_id || '';
          if (!groupMap[parkingId]) {
            groupMap[parkingId] = {
              _id: parkingId,
              parking_id: parkingId,
              parking_details: slot.parking_details || undefined,
              slots: [],
            };
          }
          // Strip parking_details from individual slot to keep it clean
          const { parking_details, organization_details, ...slotData } = slot;
          groupMap[parkingId].slots.push(slotData);
        });

        const groups: IParkingSlotGroup[] = Object.values(groupMap);
        dispatch(setCalendarSlots(groups));
        return Promise.resolve(groups);
      } catch (error) {
        return Promise.reject(error);
      }
    };


export const getOrgParkings =
  (payload: { org_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { org_id } = payload;
      const {
        data: { data: responseData },
      } = await api.get(`/admin/org/${org_id}`);
      const orgData = Array.isArray(responseData) ? responseData[0] : responseData;
      const parkings: IParkingOption[] = (orgData?.parking_details || []).map(
        (p: any) => ({
          _id: p._id || p.parking_id || '',
          address_title: p.address_title || p.city || 'Parking',
          city: p.city,
          state: p.state,
          country: p.country,
          address1: p.address1,
          address2: p.address2,
          zipcode: p.zipcode,
        }),
      );
      dispatch(setOrgParkings(parkings));
      return Promise.resolve(parkings);
    } catch (error) {
      return Promise.reject(error);
    }
  };

/**
 * Fetch available (not fully booked) slots for a given organization and date.
 * Used in the "Book Workshop" form when a vehicle is selected.
 * Calls GET /admin/slots with list_workshops=false so no heavy workshop joins.
 * Returns a flat ISlot[] sorted by start_time.
 */
export const getAvailableBookingSlotsAction =
  (
    payload: {
      organization_id: string;
      day: string; // DD/MM/YYYY
      parking_id?: string;
    },
  ) =>
    async (dispatch: Dispatch) => {
      try {
        const { organization_id, day, parking_id } = payload;
        let queryString = `?view_type=daily&day=${encodeURIComponent(day)}&organization_id=${encodeURIComponent(organization_id)}&list_workshops=false`;
        if (parking_id) {
          queryString += `&parking_id=${encodeURIComponent(parking_id)}`;
        }
        const { data: responseData } = await api.get(`/admin/slots${queryString}`);
        const slots: ISlot[] = (responseData?.data || [])
          .map(normalizeSlot)
          .sort((a: ISlot, b: ISlot) => a.start_time.localeCompare(b.start_time));
        dispatch(setAvailableBookingSlots(slots));
        return Promise.resolve(slots);
      } catch (error) {
        dispatch(setAvailableBookingSlots([]));
        return Promise.reject(error);
      }
    };

// ── Exports ────────────────────────────────────────────────────────────────────

export const {
  setCalendarSlots,
  setSelectedSlot,
  setSlotWorkshops,
  setOrgParkings,
  clearSlotWorkshops,
  setAvailableBookingSlots,
  clearAvailableBookingSlots,
} = slotsSlice.actions;

export default slotsSlice.reducer;
